import { NS } from '@/common/namespaces';
import { convertUnit, getTypeMap, isValidUnit } from "@/common/units";
import { blazorAlert } from "@/common/utilities";
import { SvgAnnotations } from "@/components";
import SvgEditService from '@/services/svg-edit-service';
import SvgCanvas from "@/svgcanvas/svgcanvas";
import Editor from "@/editor/Editor";

const { $id, isNullish } = SvgCanvas;

/*
 * register actions for left panel
 */
/**
 *
 */
export class TopPanelLegacy {
    public editor;
    public svgCanvas;
    public i18next;

    /**
     * @param {PlainObject} editor svgedit handler
     */
    constructor(editor: Editor) {
        this.editor = editor;
    }
    /**
     * @type {module}
     */
    get selectedElement() {
        return this.editor.selectedElement;
    }
    /**
     * @type {module}
     */
    get multiselected() {
        return this.editor.multiselected;
    }
    /**
     * @type {module}
     */
    get path() {
        return this.editor.svgCanvas.pathActions;
    }

    /**
     *
     * @param {Element} opt
     * @param {boolean} changeElem
     * @returns {void}
     */
    setStrokeOpt(opt, changeElem?) {
        const { id } = opt;
        const bits = id.split('_');
        const [pre, val] = bits;

        if (changeElem) {
            this.svgCanvas.setStrokeAttr('stroke-' + pre, val);
        }
        opt.classList.add('current');
        const elements = Array.prototype.filter.call(opt.parentNode.children, (child) => {
            return child !== opt;
        });
        Array.from<HTMLElement>(elements).forEach(function (element) {
            element.classList.remove('current');
        });
    }

    /**
     * Updates the toolbar (colors, opacity, etc) based on the selected element.
     * This function also updates the opacity and id elements that are in the
     * context panel.
     * @returns {void}
     */
    update() {
        let i, len;
        if (!isNullish(this.selectedElement)) {
            switch (this.selectedElement.tagName) {
                case "use":
                case "image":
                case "foreignObject":
                    break;
                case "g":
                case "a": {
                    // Look for common styles
                    const childs = this.selectedElement.getElementsByTagName("*");
                    let gWidth = null;
                    for (i = 0, len = childs.length; i < len; i++) {
                        const swidth = childs[i].getAttribute("stroke-width");

                        if (i === 0) {
                            gWidth = swidth;
                        } else if (gWidth !== swidth) {
                            gWidth = null;
                        }
                    }

                    break;
                }
                default: {
                    let attr = this.selectedElement.getAttribute("stroke-linejoin") || "miter";

                    if ($id<any>("linejoin_" + attr)?.length) {
                        this.setStrokeOpt($id("linejoin_" + attr));
                    }

                    attr = this.selectedElement.getAttribute("stroke-linecap") || "butt";

                    if ($id<any>("linecap_" + attr)?.length) {
                        this.setStrokeOpt($id("linecap_" + attr));
                    }
                }
            }
        }

        const bNoFill = (this.editor.svgCanvas.getColor('fill') === 'none');
        const bNoStroke = (this.editor.svgCanvas.getColor('stroke') === 'none');

        this.editor.svgCanvas.runExtensions(
            'toolButtonStateUpdate',
        /** @type {module:svgcanvas.SvgCanvas#event:ext_toolButtonStateUpdate} */ {
                nofill: bNoFill,
                nostroke: bNoStroke
            }
        );
    }
    /**
     * Updates the context panel tools based on the selected element.
     * @returns {void}
     */
    updateContextPanel() {

        let elem = this.editor.selectedElement;
        // If element has just been deleted, consider it null
        if (!isNullish(elem) && !elem.parentNode) {
            elem = null;
        }

        const currentMode = this.editor.svgCanvas.getMode();
        const unit =
            this.editor.configObj.curConfig.baseUnit !== "px"
                ? this.editor.configObj.curConfig.baseUnit
                : null;

        const isNode = currentMode === "pathedit";
        if (!isNullish(elem)) {
            const elname = elem.nodeName;

            if (!isNode && currentMode !== "pathedit") {
                // Elements in this array already have coord fields
                if (!["line", "circle", "ellipse"].includes(elname)) {
                    let x, y;

                    // Get BBox vals for g, polyline and path
                    if (["g", "polyline", "path"].includes(elname)) {
                        const bb = this.editor.svgCanvas.getStrokedBBox([elem]);
                        if (bb) {
                            ({ x, y } = bb);
                        }
                    } else {
                        x = elem.getAttribute("x");
                        y = elem.getAttribute("y");
                    }

                    if (unit) {
                        x = convertUnit(x);
                        y = convertUnit(y);
                    }

                    $id<any>("selected_x").value = (x || 0);
                    $id<any>("selected_y").value = (y || 0);
                }
            }
        }
    }

    /**
     * @type {module}
     */
    init() {
        this.i18next = this.editor.i18next;
    }
}
